import { useEffect, useMemo, type RefObject } from 'react';

import { Popover, PopoverAnchor, PopoverContent } from 'components/ui/popover';

import type { MentionSearchType } from '../../types/mentions.service.type';
import type {
  MentionCategoryDescriptor,
  MentionPickerItem,
} from 'features/chat/utils/normalize-mention-search';
import { MentionCategoriesMenu } from './mention-categories-menu';
import { MentionCategoryItemsMenu } from './mention-category-items-menu';
import type { MentionPickerView } from './mention-picker.constants';

export type { MentionPickerView } from './mention-picker.constants';

type ContextMentionPickerProps = {
  open: boolean;
  view: MentionPickerView;
  onViewChange: (view: MentionPickerView) => void;
  category: MentionSearchType | null;
  onCategoryChange: (category: MentionSearchType | null) => void;
  categories: MentionCategoryDescriptor[];
  items: MentionPickerItem[];
  isLoading: boolean;
  isCategoriesLoading?: boolean;
  query: string;
  onSelect: (item: MentionPickerItem) => void;
  onClearFilter?: () => void;
  onClose: () => void;
  inputRef?: RefObject<HTMLElement | null>;
};

export function ContextMentionPicker({
  open,
  view,
  onViewChange,
  category,
  onCategoryChange,
  categories,
  items,
  isLoading,
  isCategoriesLoading,
  query,
  onSelect,
  onClearFilter,
  onClose,
  inputRef,
}: ContextMentionPickerProps) {
  const hasSearchQuery = query.trim().length > 0;

  const categoryItems = useMemo(() => {
    if (!category) return items;
    return items.filter((item) => item.type === category);
  }, [items, category]);

  // Typing after / skips categories and shows API search results directly.
  useEffect(() => {
    if (!open) return;
    if (hasSearchQuery) {
      onViewChange('items');
      onCategoryChange(null);
      return;
    }
    if (!category && view === 'items') {
      onViewChange('categories');
    }
  }, [hasSearchQuery, open, category, view, onViewChange, onCategoryChange]);

  const handleCategorySelect = (type: MentionSearchType) => {
    onCategoryChange(type);
    onViewChange('items');
  };

  const handleClearFilter = () => {
    if (hasSearchQuery) {
      onClearFilter?.();
    }

    onCategoryChange(null);
    onViewChange('categories');
  };

  if (!open) return null;

  const showSearchResults = hasSearchQuery;
  const showCategoryItems = !showSearchResults && view === 'items' && category;
  const showCategories = !showSearchResults && view === 'categories';

  // A popover (not an absolute div) so it flips below or shrinks when the input
  // sits too close to the top of the page, instead of being cut off.
  return (
    <Popover open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <PopoverAnchor className="pointer-events-none absolute inset-x-0 top-0 h-0" />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // ChatInput steps back through the picker on Escape; don't let Radix close it outright.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (inputRef?.current?.contains(event.target as Node)) event.preventDefault();
        }}
        onMouseDown={(event) => event.preventDefault()}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-56 min-w-0 items-stretch overflow-y-auto border-0 bg-transparent p-0 shadow-none dark:border-0 dark:bg-transparent"
      >
        {showSearchResults ? (
          <MentionCategoryItemsMenu
            items={items}
            isLoading={isLoading}
            query={query}
            onSelect={onSelect}
            onClearFilter={handleClearFilter}
          />
        ) : null}
        {showCategoryItems ? (
          <MentionCategoryItemsMenu
            category={category}
            items={categoryItems}
            isLoading={isLoading}
            query={query}
            onSelect={onSelect}
            onClearFilter={handleClearFilter}
          />
        ) : null}
        {showCategories ? (
          <MentionCategoriesMenu
            categories={categories}
            isLoading={isCategoriesLoading ?? isLoading}
            onSelectCategory={handleCategorySelect}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
